import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
import { inject, onMounted, ref, watch, nextTick } from 'vue';
import { Canvas, StaticCanvas, Textbox, Rect, Circle, Line, FabricImage } from 'fabric';
import { jsPDF } from 'jspdf';
// Variável para controlar recursões infinitas
export default /*@__PURE__*/ _defineComponent({
    __name: 'designCanvas',
    props: /*@__PURE__*/ _mergeModels({
        editable: { type: Boolean, default: false },
        view: { default: 'front' },
        replace: { default: () => ({}) }
    }, {
        "modelValue": {},
        "modelModifiers": {},
    }),
    emits: ["update:modelValue"],
    setup(__props, { expose: __expose }) {
        const isUpdating = ref(false);
        const selectedLayer = inject('selectedLayer', null);
        const selectedIndex = inject('selectedIndex', ref(-1));
        const loadedImages = [];
        const model = _useModel(__props, "modelValue");
        const frontPageRef = ref(null);
        const backPageRef = ref(null);
        let frontCanvas = null;
        let backCanvas = null;
        // Este manipulador de eventos atualiza o modelo quando um objeto é modificado.
        const onObjectModified = (e) => {
            const obj = e.target;
            if (obj && obj.layerIndex !== undefined) {
                const index = obj.layerIndex;
                const updatedLayers = [...model.value.layers];
                const updatedLayer = { ...updatedLayers[index] };
                updatedLayer.rect = {
                    x: obj.left || 0,
                    y: obj.top || 0,
                    w: obj.width,
                    h: obj.height ? obj.height * obj.scaleY : updatedLayer.rect.h,
                };
                updatedLayer.rotation = obj.angle || 0;
                // Se o objeto for um Textbox atualiza o conteúdo do texto no model
                if (obj.type === 'textbox' && updatedLayer.text) {
                    updatedLayer.text.content = obj.text;
                }
                updatedLayer.rect.scaleX = obj.scaleX || 1;
                updatedLayer.rect.scaleY = obj.scaleY || 1;
                updatedLayers[index] = updatedLayer;
                model.value = { ...model.value, layers: updatedLayers };
            }
        };
        const onBackObjectModified = (e) => {
            const obj = e.target;
            if (obj && obj.layerIndex !== undefined) {
                const index = obj.layerIndex;
                const updatedLayers = [...model.value.backLayers];
                const updatedLayer = { ...updatedLayers[index] };
                updatedLayer.rect = {
                    x: obj.left || 0,
                    y: obj.top || 0,
                    w: obj.width,
                    h: obj.height ? obj.height * obj.scaleY : updatedLayer.rect.h,
                };
                updatedLayer.rotation = obj.angle || 0;
                // Se o objeto for um Textbox atualiza o conteúdo do texto no model
                if (obj.type === 'textbox' && updatedLayer.text) {
                    updatedLayer.text.content = obj.text;
                }
                updatedLayer.rect.scaleX = obj.scaleX || 1;
                updatedLayer.rect.scaleY = obj.scaleY || 1;
                updatedLayers[index] = updatedLayer;
                model.value = { ...model.value, backLayers: updatedLayers };
            }
        };
        const initCanvas = async () => {
            if (!frontPageRef.value || !backPageRef.value)
                return;
            frontCanvas = (__props.editable !== false) ? new Canvas(frontPageRef.value) : new StaticCanvas(frontPageRef.value);
            backCanvas = (__props.editable !== false) ? new Canvas(backPageRef.value) : new StaticCanvas(backPageRef.value);
            const { width, height } = model.value?.settings?.page || {};
            // Aplica as dimenções do canvas, ou utiliza o aspecto A4, paisagem, multiplicado por 3
            frontCanvas.setDimensions({
                width: width || (297 * 3),
                height: height || (210 * 3)
            });
            backCanvas.setDimensions({
                width: width || (297 * 3),
                height: height || (210 * 3)
            });
            updateLayers();
            if (__props.editable !== false) {
                frontCanvas.on('object:modified', onObjectModified);
                backCanvas.on('object:modified', onBackObjectModified);
            }
        };
        const updateBackground = async () => {
            if (!frontCanvas)
                return;
            const frontBg = model.value.background;
            const backBg = model.value.backBackground;
            frontCanvas.backgroundColor = frontBg?.color || '#FFF';
            backCanvas.backgroundColor = backBg?.color || '#FFF';
            if (frontBg && frontBg?.img?.url) {
                FabricImage.fromURL(frontBg.img.url, { crossOrigin: 'anonymous' }).then((img) => {
                    img.set({
                        left: 0,
                        top: 0,
                        scaleX: frontCanvas.width / img.width,
                        scaleY: frontCanvas.height / img.height
                    });
                    frontCanvas.backgroundImage = img;
                    frontCanvas.renderAll();
                });
            }
            if (backBg && backBg?.img?.url) {
                FabricImage.fromURL(backBg.img.url, { crossOrigin: 'anonymous' }).then((img) => {
                    img.set({
                        left: 0,
                        top: 0,
                        scaleX: backCanvas.width / img.width,
                        scaleY: backCanvas.height / img.height
                    });
                    backCanvas.backgroundImage = img;
                    backCanvas.renderAll();
                });
            }
        };
        const replaceText = (text) => {
            if (!__props.replace)
                return text;
            let replacedText = text;
            for (const tag in __props.replace) {
                if (Object.prototype.hasOwnProperty.call(__props.replace, tag)) {
                    const regExp = new RegExp(tag, 'gi');
                    replacedText = replacedText.replace(regExp, (match) => {
                        const value = __props.replace[tag]?.toString() || '';
                        // Se a tag encontrada no template estiver toda em maiúsculas, converte o valor para maiúsculas
                        if (match === match.toUpperCase())
                            return value.toUpperCase();
                        return value;
                    });
                }
            }
            return replacedText;
        };
        const formatObject = async (layer) => {
            let obj = null;
            if (layer.type === 'text' && layer.text) {
                obj = new Textbox(replaceText(layer.text?.content || ''), {
                    left: layer.rect.x,
                    top: layer.rect.y,
                    width: layer.rect.w || 200,
                    fontFamily: layer.text?.fontFamily || 'Arial',
                    fontSize: layer.text?.fontSize || 16,
                    fontWeight: layer.text?.fontWeight || 'normal',
                    fontStyle: layer.text?.fontStyle || 'normal',
                    underline: layer.text?.underline || false,
                    angle: layer.rotation || 0,
                    textAlign: layer.text?.textAlign || 'left',
                    fill: layer.text?.fill || 'black',
                    lineHeight: layer.text?.lineHeight || 0.9
                });
            }
            else if (layer.type === 'image' && layer.img?.url) {
                const url = layer.img.url;
                let img = loadedImages.find((i) => i.url === url)?.data;
                if (!img) {
                    img = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
                    loadedImages.push({ url, data: img });
                    // Força a atualização do canvas para exibir a imagem na ordem correta
                    updateLayers();
                    return;
                }
                img.set({
                    left: layer.rect.x,
                    top: layer.rect.y,
                    angle: layer.rotation || 0,
                    scaleX: layer.rect.scaleX || 1,
                    scaleY: layer.rect.scaleY || 1
                });
                obj = img;
            }
            else if (layer.type === 'object' && layer.object) {
                // QRcode
                if (layer.object.type === 'qrcode') {
                    const url = !layer.object?.url
                        ? 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=Exemplo'
                        : ('https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(layer.object.url));
                    let img = loadedImages.find((i) => i.url === url)?.data;
                    if (!img) {
                        img = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
                        loadedImages.push({ url, data: img });
                        updateLayers();
                        return;
                    }
                    img.set({
                        left: layer.rect.x,
                        top: layer.rect.y,
                        angle: layer.rotation || 0,
                        scaleX: layer.rect.scaleX || 1,
                        scaleY: layer.rect.scaleY || 1
                    });
                    obj = img;
                }
                if (layer.object.type === 'rect') {
                    obj = new Rect({
                        left: layer.rect.x,
                        top: layer.rect.y,
                        width: layer.rect.w || 50,
                        height: layer.rect.h || 50,
                        fill: 'blue',
                        angle: layer.rotation || 0,
                        opacity: layer.opacity !== undefined ? layer.opacity / 100 : 1,
                        flipX: layer.flipX || false,
                        flipY: layer.flipY || false
                    });
                }
                else if (layer.object.type === 'circle') {
                    obj = new Circle({
                        left: layer.rect.x,
                        top: layer.rect.y,
                        radius: (layer.rect.w || 50) / 2,
                        fill: 'green',
                        angle: layer.rotation || 0,
                        opacity: layer.opacity !== undefined ? layer.opacity / 100 : 1
                    });
                }
                else if (layer.object.type === 'line') {
                    obj = new Line([
                        layer.rect.x,
                        layer.rect.y,
                        layer.rect.x + (layer.rect.w || 50),
                        layer.rect.y + (layer.rect.h || 50)
                    ], {
                        stroke: 'red',
                        angle: layer.rotation || 0,
                        opacity: layer.opacity !== undefined ? layer.opacity / 100 : 1
                    });
                }
            }
            return obj;
        };
        const updateLayers = () => {
            if (!frontCanvas && !backCanvas)
                return;
            if (isUpdating.value)
                return;
            isUpdating.value = true;
            // Remove as camadas marcadas como "remover"
            model.value.layers = model.value.layers.filter((layer) => !layer.remove);
            model.value.backLayers = model.value.backLayers?.filter((layer) => !layer.remove);
            // Limpa o canvas
            frontCanvas?.clear();
            backCanvas?.clear();
            // Reaplica o fundo (Por causa do clear())
            updateBackground();
            model.value.layers.forEach(async (layer, index) => {
                const obj = await formatObject(layer);
                if (obj) {
                    obj.layerIndex = index;
                    if (__props.editable !== false) {
                        // @ts-ignore
                        obj.on('selected', () => {
                            selectedLayer.value = layer;
                            selectedIndex.value = index;
                        });
                    }
                    frontCanvas.add(obj);
                }
            });
            model.value.backLayers?.forEach(async (layer, index) => {
                const obj = await formatObject(layer);
                if (obj) {
                    obj.layerIndex = index;
                    // @ts-ignore
                    obj.toObject(['layerIndex', 'id']);
                    if (__props.editable !== false) {
                        // @ts-ignore
                        obj.on('selected', () => {
                            selectedLayer.value = layer;
                            selectedIndex.value = index;
                        });
                    }
                    backCanvas.add(obj);
                }
                // Gambiarra para "multiplas colunas" de #CONTENT#
                if (layer.text?.content.includes('#CONTENT#') && __props.replace['#CONTENT2#']) {
                    const textObj = new Textbox(__props.replace['#CONTENT2#'], {
                        left: layer.rect.x + (layer.rect.w || 200),
                        top: layer.rect.y,
                        width: layer.rect.w || 200,
                        fontFamily: layer.text.fontFamily || 'Arial',
                        fontSize: layer.text.fontSize || 16,
                        fontWeight: layer.text.fontWeight || 'normal',
                        fontStyle: layer.text.fontStyle || 'normal',
                        underline: layer.text.underline || false,
                        angle: layer.rotation || 0,
                        textAlign: layer.text.textAlign || 'left',
                        fill: layer.text.fill || 'black',
                        lineHeight: layer.text.lineHeight || 0.9
                    });
                    backCanvas.add(textObj);
                }
            });
            nextTick(() => {
                isUpdating.value = false;
            });
        };
        const exportToPNG = () => {
            if (!frontCanvas)
                return;
            // Generate the base64-encoded PNG image data
            const dataURL = frontCanvas.toDataURL({
                format: 'png',
                quality: 1.0,
                multiplier: 1
            });
            const link = document.createElement('a');
            link.href = dataURL;
            link.download = 'canvas-image.jpeg';
            link.click();
        };
        const exportToPDF = () => {
            if (!frontCanvas)
                return;
            // Generate a JPEG data URL of the canvas content.
            const dataURL = frontCanvas.toDataURL({
                format: 'jpeg',
                quality: 1.0,
                multiplier: 3
            });
            // Create a jsPDF instance using the canvas dimensions.
            const pdf = new jsPDF({
                orientation: frontCanvas.width > frontCanvas.height ? 'landscape' : 'portrait',
                unit: 'px',
                format: [frontCanvas.width, frontCanvas.height]
            });
            // Add the canvas image to the PDF.
            pdf.addImage(dataURL, 'JPEG', 0, 0, frontCanvas.width, frontCanvas.height);
            // Save the PDF document.
            pdf.save('canvas-document.pdf');
        };
        onMounted(() => {
            initCanvas();
        });
        // Observa alterações no modelo (por exemplo, quando camadas são adicionadas/removidas externamente)
        watch(() => model.value, (newVal) => {
            if (frontCanvas) {
                frontCanvas.setDimensions({
                    width: Number(newVal.settings?.page?.width) || (297 * 3),
                    height: Number(newVal.settings?.page?.height) || (210 * 3)
                });
            }
            if (backCanvas) {
                backCanvas.setDimensions({
                    width: Number(newVal.settings?.page?.width) || (297 * 3),
                    height: Number(newVal.settings?.page?.height) || (210 * 3)
                });
            }
            updateLayers();
        }, { deep: true });
        __expose({
            exportToPNG,
            exportToPDF
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                _withDirectives(_createElementVNode("div", null, [
                    _createElementVNode("canvas", {
                        ref_key: "frontPageRef",
                        ref: frontPageRef,
                        class: "!max-w-full !h-auto"
                    }, null, 512)
                ], 512), [
                    [_vShow, !model.value.settings?.page?.backSide || ['both', 'front'].includes(_ctx.view)]
                ]),
                _withDirectives(_createElementVNode("div", null, [
                    _createElementVNode("canvas", {
                        ref_key: "backPageRef",
                        ref: backPageRef,
                        class: "!max-w-full !h-auto"
                    }, null, 512)
                ], 512), [
                    [_vShow, model.value.settings?.page?.backSide && ['both', 'back'].includes(_ctx.view)]
                ])
            ]));
        };
    }
});
